import { useRuntimeState } from '../app/use-runtime-state'
import { HITS_TO_STUN, MAGAZINE_CAPACITY, RELOAD_SECONDS } from '../game/combat'
import type { GameRuntime } from '../game/runtime'
import type { CameraMode } from '../scene/FollowCamera'
import { Leaderboard } from './Leaderboard'
import { Minimap } from './Minimap'
import { ProximityRadar } from './ProximityRadar'
import { formatOrdinal } from './race-format'

interface HudProps {
  runtime: GameRuntime
  cameraMode: CameraMode
}

const DRIVER_PRESSURE_PIPS = [1, 2, 3, 4] as const

function Meter({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
  const normalized = Math.min(100, Math.max(0, value))
  return (
    <div className={`meter ${danger ? 'meter--danger' : ''}`}>
      <div className="meter__label">
        <span>{label}</span>
        <output>{Math.round(normalized).toString().padStart(3, '0')}</output>
      </div>
      <div className="meter__track">
        <span style={{ width: `${normalized}%` }} />
      </div>
    </div>
  )
}

export function Hud({ runtime, cameraMode }: HudProps) {
  const state = useRuntimeState(runtime)
  const playerId = state.race.playerId
  const player = state.vehicles.find((vehicle) => vehicle.id === playerId)
  const progress = state.race.progress[playerId]
  if (!player || !progress) return null
  const position = state.ranking.indexOf(playerId) + 1
  const speed = Math.round(Math.abs(player.speed) * 5.4)
  const lap = Math.min(state.race.totalLaps, progress.lap + 1)
  const reloadProgress = player.reloadTimer > 0 ? (1 - player.reloadTimer / RELOAD_SECONDS) * 100 : 0
  return (
    <div className="hud" aria-live="polite">
      <header className="hud__top">
        <div className="race-place">
          <span>POSITION</span>
          <strong>
            {formatOrdinal(position)} / {state.ranking.length}
          </strong>
        </div>
        <div className="race-brand">TRIDE {'//'} NIGHT GRID</div>
        <div className="race-lap">
          <span>LAP</span>
          <strong>
            {lap} / {state.race.totalLaps}
          </strong>
        </div>
      </header>
      <Leaderboard state={state} />
      <Minimap state={state} />
      <ProximityRadar state={state} />
      <div className="reticle" aria-hidden="true">
        <span />
      </div>
      {state.race.phase === 'countdown' && (
        <div className="countdown" role="status" aria-label={`Race starts in ${Math.ceil(state.race.countdown)}`}>
          <span>{Math.max(1, Math.ceil(state.race.countdown))}</span>
          <small>IGNITION ARMED</small>
        </div>
      )}
      {state.race.phase === 'finish-countdown' && (
        <div
          className="final-call"
          role="status"
          aria-label={`Race ends in ${Math.ceil(state.race.finishCountdown ?? 0)} seconds`}
        >
          <small>FINAL CALL</small>
          <strong>{Math.max(1, Math.ceil(state.race.finishCountdown ?? 0))}</strong>
          <span>FINISH THE LAP</span>
        </div>
      )}
      {player.drift.active && (
        <div className="drift-callout">
          <span>DRIFT LINK</span>
          <strong>+{Math.round(player.drift.charge * 10)}</strong>
        </div>
      )}
      {player.reloadTimer > 0 && <div className="warning-callout">RELOADING</div>}
      {player.driverStunTimer > 0 && <div className="warning-callout">DRIVER HIT</div>}
      {player.offTrack && (
        <div className="warning-callout warning-callout--overload">
          RETURN TO TRACK {'//'} ENGINE LOAD {Math.round(player.engineFatigue)}%
        </div>
      )}
      {player.driverStunTimer > 0 && <div className="driver-hit-vignette" aria-hidden="true" />}
      <footer className="hud__bottom">
        <div className="speed-block">
          <output>{speed.toString().padStart(3, '0')}</output>
          <span>KM/H</span>
        </div>
        <div className="status-meters">
          <Meter label="BOOST" value={player.boost} />
          <Meter label="ENGINE FATIGUE" value={player.engineFatigue} danger={player.engineFatigue > 0} />
          <div className="pistol-status">
            <span>PISTOL</span>
            <strong>
              {player.ammo.toString().padStart(2, '0')} / {MAGAZINE_CAPACITY}
            </strong>
            {player.reloadTimer > 0 && (
              <div
                className="pistol-status__reload"
                role="progressbar"
                aria-label="Reload progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(reloadProgress)}
              >
                <span style={{ width: `${reloadProgress}%` }} />
              </div>
            )}
          </div>
          <div className="driver-pressure" data-testid="driver-pressure">
            <span>DRIVER</span>
            <strong>
              {player.driverHitCount} / {HITS_TO_STUN}
            </strong>
            <div className="driver-pressure__pips" aria-hidden="true">
              {DRIVER_PRESSURE_PIPS.map((slot) => (
                <i key={slot} data-active={slot <= player.driverHitCount || undefined} />
              ))}
            </div>
          </div>
        </div>
        <div className="camera-mode">
          CAM {'//'} <strong>{cameraMode.toUpperCase()}</strong>
        </div>
      </footer>
    </div>
  )
}
